import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import axios from '../../api/axios';
import { logout } from '../../services/keycloak/authService';
import SecuritySettings from '../admin/SecuritySettings';
import OrgSettings from '../workspace/OrgSettings';
import Banner from '../../ui/Banner';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import './Settings.css';

function Settings({ currentUser, setCurrentUser }) {
  const { isOrganizationAdmin, isOrganizationWorkspace, workspaceName } = useWorkspace();
  const isOrganizationAccount =
    isOrganizationWorkspace || currentUser?.account_type === 'organization';
  const location = useLocation();
  const passwordSectionRef = useRef(null);
  const accountSectionRef = useRef(null);
  const securitySectionRef = useRef(null);
  const isSocialMember = currentUser?.isOAuthUser === true;

  const [companyDetails, setCompanyDetails] = useState(null);
  const [companyDetailsLoading, setCompanyDetailsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('account');

  const [emailChange, setEmailChange] = useState({ email: '' });
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeMessage, setEmailChangeMessage] = useState({ type: '', text: '' });

  const [usernameChange, setUsernameChange] = useState({ username: '' });
  const [usernameChangeLoading, setUsernameChangeLoading] = useState(false);
  const [usernameChangeMessage, setUsernameChangeMessage] = useState({ type: '', text: '' });
  const [isEditingUsername, setIsEditingUsername] = useState(false);

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const togglePasswordVisibility = (fieldName) => {
    setShowPasswords((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        setCompanyDetailsLoading(true);
        const response = await axios.get('/user/context');
        if (response.data.success) {
          const workspace = response.data.userContext.primaryWorkspace;
          if (workspace && workspace.workspaceType === 'organization') {
            const workspaceResponse = await axios.get(
              `/workspace/${workspace.workspaceId}/settings`
            );
            if (workspaceResponse.data.success) {
              setCompanyDetails(workspaceResponse.data.workspace);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch company details:', error);
      } finally {
        setCompanyDetailsLoading(false);
      }
    };

    if (currentUser) {
      fetchCompanyDetails();
    }
  }, [currentUser]);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const section = location.state.scrollTo;
      let targetRef = null;

      if (section === 'password') {
        targetRef = passwordSectionRef;
      } else if (section === 'account') {
        targetRef = accountSectionRef;
      } else if (section === 'security') {
        targetRef = securitySectionRef;
      }

      if (targetRef?.current) {
        setTimeout(() => {
          targetRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUsernameInputChange = (e) => {
    setUsernameChange({ username: e.target.value });
  };

  const handleUsernameChangeSubmit = async (e) => {
    e.preventDefault();
    setUsernameChangeMessage({ type: '', text: '' });

    const newUsername = usernameChange.username.trim();
    const usernamePattern = /^[a-zA-Z0-9_]{1,26}$/;

    if (!newUsername) {
      setUsernameChangeMessage({ type: 'error', text: 'Username is required' });
      return;
    }

    if (!usernamePattern.test(newUsername)) {
      setUsernameChangeMessage({
        type: 'error',
        text: 'Username can only include letters, numbers, and underscores, and must be 1-26 characters long',
      });
      return;
    }

    if (currentUser?.username && currentUser.username === newUsername) {
      setUsernameChangeMessage({ type: 'error', text: 'New username must be different' });
      return;
    }

    setUsernameChangeLoading(true);

    try {
      const response = await axios.post('/profile/change-username', { username: newUsername });

      if (response.data?.success) {
        localStorage.setItem('username', newUsername);
        if (setCurrentUser) {
          setCurrentUser((prev) => ({
            ...prev,
            username: newUsername,
          }));
        }

        setUsernameChangeMessage({
          type: 'success',
          text: 'Username updated successfully!',
        });
        setUsernameChange({ username: '' });
        setIsEditingUsername(false);

        if (response.data?.requireTokenRefresh) {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      } else {
        setUsernameChangeMessage({
          type: 'error',
          text: response.data?.message || 'Failed to update username',
        });
      }
    } catch (error) {
      setUsernameChangeMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update username',
      });
    } finally {
      setUsernameChangeLoading(false);
    }
  };

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
    return requirements;
  };

  const isPasswordValid = (password) => {
    const reqs = validatePassword(password);
    return Object.values(reqs).every((v) => v === true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.newPassword || !formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New password and confirm password are required' });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    const passwordReqs = validatePassword(formData.newPassword);
    if (!passwordReqs.length) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }
    if (!passwordReqs.uppercase) {
      setMessage({ type: 'error', text: 'Password must contain at least one uppercase letter' });
      return;
    }
    if (!passwordReqs.lowercase) {
      setMessage({ type: 'error', text: 'Password must contain at least one lowercase letter' });
      return;
    }
    if (!passwordReqs.number) {
      setMessage({ type: 'error', text: 'Password must contain at least one number' });
      return;
    }
    if (!passwordReqs.special) {
      setMessage({
        type: 'error',
        text: "Password must contain at least one special character (!@#$%^&*()_+-=[]{};':\\|,.<>/?)",
      });
      return;
    }

    if (formData.currentPassword && formData.currentPassword === formData.newPassword) {
      setMessage({ type: 'error', text: 'New password must be different from current password' });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      setMessage({
        type: 'success',
        text: 'Password changed successfully! Logging out all devices for security... Logging out all devices...',
      });

      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      setTimeout(async () => {
        try {
          await logout();
        } catch (logoutError) {
          console.error('Error during logout:', logoutError);
          window.location.href = '/login';
        }
      }, 2000);
    } catch (error) {
      const errorText =
        error.response?.data?.message || error.message || 'Failed to change password';
      setMessage({ type: 'error', text: errorText });
    } finally {
      setLoading(false);
    }
  };

  const renderAccountSettings = () => (
    <div className={`settings-two-column ${isSocialMember ? 'settings-oauth-single-column' : ''}`}>
      {/* Account Information Section */}
      <div className="settings-section" ref={accountSectionRef}>
        <div className="section-header">
          <h2>Account Information</h2>
          <p>Your account details and authentication information</p>
        </div>

        <div className="account-info">
          <div className="info-item">
            <div className="info-with-action">
              <div>
                <label>Username</label>
                {!isEditingUsername && <p>{currentUser?.username || 'N/A'}</p>}
              </div>
              {!isEditingUsername && (
                <button
                  className="btn-icon"
                  onClick={() => {
                    setIsEditingUsername(true);
                    setUsernameChange({ username: currentUser?.username || '' });
                    setUsernameChangeMessage({ type: '', text: '' });
                  }}
                  title="Edit Username"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                  }}
                >
                  <i className="fas fa-pen"></i>
                </button>
              )}
            </div>

            {isEditingUsername && (
              <form onSubmit={handleUsernameChangeSubmit} className="email-change-form">
                <input
                  type="text"
                  name="newUsername"
                  value={usernameChange.username}
                  onChange={handleUsernameInputChange}
                  placeholder="Enter new username"
                  autoComplete="username"
                  autoFocus
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="submit" className="btn-outline" disabled={usernameChangeLoading}>
                    {usernameChangeLoading ? 'Updating...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn-outline danger"
                    onClick={() => setIsEditingUsername(false)}
                    disabled={usernameChangeLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {usernameChangeMessage.text && (
              <Banner
                type={usernameChangeMessage.type}
                description={usernameChangeMessage.text}
                className="banner-compact"
              />
            )}

            {isEditingUsername && (
              <div className="info-notice">
                <i className="fas fa-info-circle"></i>
                <span>a-Z, 0-9, and _ only (Max 26 characters)</span>
              </div>
            )}
          </div>

          <div className="info-item">
            <label>Email Address</label>
            <p>{currentUser?.email || 'N/A'}</p>

            {isOrganizationAccount ? (
              <div className="info-notice" style={{ color: '#94a3b8', marginTop: '8px' }}>
                <i className="fas fa-lock" style={{ fontSize: '12px' }}></i>
                <span>Email cannot be changed for organization accounts</span>
              </div>
            ) : (
              <>
                <div className="info-notice">
                  <i className="fas fa-info-circle"></i>
                  <span>You cannot change your email address by yourself</span>
                </div>
                <small className="email-help">
                  To change your email, please contact support using the Chatbot Icon located at
                  Help Center. <Link to="/help">Go to Help Center</Link>
                </small>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="settings-section" ref={passwordSectionRef}>
        <div className="section-header">
          <h2>Change Password</h2>
          {isSocialMember ? (
            <p style={{ color: '#6b7280', fontSize: '13px' }}>
              Signed in via Google - no password required
            </p>
          ) : (
            <p>Update your password to keep your account secure</p>
          )}
        </div>

        {isSocialMember ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '16px',
              background: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              color: '#6c757d',
              fontSize: '14px',
            }}
          >
            <i className="fab fa-google" style={{ marginRight: '8px', color: '#4285F4' }}></i>
            <span>Your account is linked to Google. You don't have a password to change.</span>
          </div>
        ) : (
          <form onSubmit={handleChangePassword} className="change-password-form">
            {message.text && (
              <Banner type={message.type} description={message.text} className="banner-compact" />
            )}

            <div className="form-group">
              <label htmlFor="currentPassword">
                Current Password{' '}
                <span style={{ fontWeight: 400, color: '#6b7280' }}>(optional)</span>
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPasswords.currentPassword ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                  placeholder="Enter current password"
                  style={{ paddingRight: '40px', width: '100%' }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('currentPassword')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    fontSize: '18px',
                    padding: '4px 8px',
                  }}
                  title={showPasswords.currentPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas fa-eye${showPasswords.currentPassword ? '' : '-slash'}`}></i>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPasswords.newPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  required
                  style={{ paddingRight: '40px', width: '100%' }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('newPassword')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    fontSize: '18px',
                    padding: '4px 8px',
                  }}
                  title={showPasswords.newPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas fa-eye${showPasswords.newPassword ? '' : '-slash'}`}></i>
                </button>
              </div>
              <small>
                Must be at least 8 characters with uppercase, lowercase, number, and special
                character
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPasswords.confirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm new password"
                  required
                  style={{ paddingRight: '40px', width: '100%' }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    fontSize: '18px',
                    padding: '4px 8px',
                  }}
                  title={showPasswords.confirmPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fas fa-eye${showPasswords.confirmPassword ? '' : '-slash'}`}></i>
                </button>
              </div>
            </div>

            <button type="submit" className="btn-change-password" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-lock"></i> Change Password
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div className="settings-container">
      <Banner
        title="Settings"
        description={`Customize your profile, account preferences, and security settings to enhance your experience.${isOrganizationAccount ? ' Manage your organization details and team configurations here.' : ''}`}
        type="page-header"
      />

      {isOrganizationAccount && (
        <>
          <div className="settings-section-tabs">
            <button
              className={`settings-section-tab ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              <i className="fas fa-user-circle"></i> Account
            </button>
            <div className="settings-tab-divider"></div>
            <button
              className={`settings-section-tab ${activeTab === 'organization' ? 'active' : ''}`}
              onClick={() => setActiveTab('organization')}
            >
              <i className="fas fa-building"></i>{' '}
              {isOrganizationAdmin ? 'Organization' : 'Company Profile'}
            </button>
          </div>
          <div className="settings-horizontal-divider"></div>
        </>
      )}

      <div className="settings-content">
        {isOrganizationAccount ? (
          <>
            {activeTab === 'account' && renderAccountSettings()}
            {activeTab === 'organization' && <OrgSettings embedded />}
          </>
        ) : (
          <>
            {renderAccountSettings()}
            {currentUser?.isAdmin && (
              <div className="settings-section" ref={securitySectionRef}>
                <div className="section-header">
                  <h2>Security Settings</h2>
                  <p>Manage multi-factor authentication for your account</p>
                </div>
                <SecuritySettings embedded />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Settings;
